/**
 * Client-side year-matrix XLSX (PRD §9, PLAN.md D-45/D-46). Built with `xlsx` from data already
 * on the calendar / Live view — no synthesis of hours that were never tracked.
 */

import * as XLSX from "xlsx";
import { rdSharePct } from "@/shared/aggregation";
import { isoWeekOf, isoWeekday, type BusinessDate } from "@/shared/dates";
import { formatPersonName } from "@/shared/person-name";
import type { SessionRole } from "@/shared/schemas/auth";
import type { CalendarMonth, LiveOverview } from "@/shared/schemas/tracking";

/** Fixed DE abbreviations for the employee export contract (reference calendar columns). */
const WEEKDAYS_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
/** English abbreviations for the project Live year-matrix export. */
const WEEKDAYS_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS_EN = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function safeName(value: string): string {
  return value.replace(/[^\w.\- ]+/g, "_").trim() || "Unbenannt";
}

function absenceLabelDe(type: string | null): string {
  if (type === "vacation") return "Urlaub";
  if (type === "sick") return "Krank";
  if (type === "other") return "Sonstiges";
  return "";
}

function absenceLabelEn(type: string | null): string {
  if (type === "vacation") return "Vacation";
  if (type === "sick") return "Sick";
  if (type === "other") return "Other";
  return "";
}

function weekdayDe(entryDate: BusinessDate): string {
  return WEEKDAYS_DE[isoWeekday(entryDate) - 1]!;
}

function weekdayEn(entryDate: BusinessDate): string {
  return WEEKDAYS_EN[isoWeekday(entryDate) - 1]!;
}

function downloadWorkbook(workbook: XLSX.WorkBook, filename: string): void {
  XLSX.writeFile(workbook, filename);
}

export type EmployeeYearMatrixNames = {
  projectName: string;
  lastName: string;
};

/** Employee year sheet — DE headers 1:1 with the reference (PLAN.md 5.8). */
export function exportEmployeeYearMatrix(
  calendar: CalendarMonth,
  names: EmployeeYearMatrixNames,
): string {
  const year = calendar.year;
  const rows = calendar.days
    .filter((day) => Number(day.entryDate.slice(0, 4)) === year && day.saved)
    .map((day) => {
      const { week } = isoWeekOf(day.entryDate);
      return {
        Datum: day.entryDate,
        Wochentag: weekdayDe(day.entryDate),
        KW: week,
        Stunden: day.totalHours ?? "",
        "FuE-Stunden": day.rdHours ?? "",
        Abwesenheit: absenceLabelDe(day.absenceType),
        Anteil: day.absenceFraction || "",
        Feiertag: day.holiday ?? "",
        Notiz: day.weekNote ?? "",
        "Admin-Bearbeitung am": day.adminEditedAt ?? "",
      };
    });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), String(year));

  const notes = calendar.weekNotes.filter((note) => note.note.trim().length > 0);
  if (notes.length > 0) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(
        notes.map((note) => ({
          KW: note.isoWeek,
          "An was gearbeitet": note.note,
        })),
      ),
      "Wochennotizen",
    );
  }

  const filename = `Stunden_${safeName(names.projectName)}_${safeName(names.lastName)}_${year}.xlsx`;
  downloadWorkbook(workbook, filename);
  return filename;
}

/** Kept on the export signature so existing Live/admin callers stay type-compatible. */
export type YearMatrixRoleLabels = Record<SessionRole, string>;

const WEEK_NOTES_SHEET = "Week Notes (Descriptions)";
const WEEK_NOTES_HEADERS = ["Week", "Employee", "Description"] as const;
const ALL_ENTRIES_HEADERS = [
  "Date",
  "Weekday",
  "Week",
  "Employee",
  "Employee Email",
  "Project",
  "Hours",
  "R&D hours",
  "Absence",
  "Fraction",
  "Holiday",
  "Note",
  "Admin edited at",
] as const;

/** Project Live overview — English sheets: All Entries → Overview {year} → Week Notes. */
export function exportProjectYearMatrix(
  overview: LiveOverview,
  _roleLabels: YearMatrixRoleLabels,
): string {
  const year = overview.year;

  const detailRows = (overview.dayEntries ?? [])
    .slice()
    .sort(
      (a, b) =>
        a.lastName.localeCompare(b.lastName) ||
        a.firstName.localeCompare(b.firstName) ||
        a.entryDate.localeCompare(b.entryDate),
    )
    .map((entry) => {
      const { week } = isoWeekOf(entry.entryDate);
      return {
        Date: entry.entryDate,
        Weekday: weekdayEn(entry.entryDate),
        Week: week,
        Employee: formatPersonName(entry),
        "Employee Email": entry.email,
        Project: overview.projectName,
        Hours: entry.totalHours,
        "R&D hours": entry.rdHours,
        Absence: absenceLabelEn(entry.absenceType),
        Fraction: entry.absenceFraction || "",
        Holiday: entry.holiday ?? "",
        Note: entry.note ?? "",
        "Admin edited at": entry.adminEditedAt ?? "",
      };
    });

  const overviewRows = overview.employees.map((employee) => {
    const row: Record<string, string | number> = {
      FirstName: employee.firstName,
      LastName: employee.lastName,
      Email: employee.email,
    };
    let yearTotal = 0;
    let yearRd = 0;
    for (let month = 1; month <= 12; month += 1) {
      const cell = employee.months[month - 1];
      const total = cell?.totalHours ?? 0;
      const rd = cell?.rdHours ?? 0;
      row[MONTHS_EN[month - 1]!] = total;
      row[`${MONTHS_EN[month - 1]!} R&D`] = rd;
      yearTotal += total;
      yearRd += rd;
    }
    row["Year total"] = yearTotal;
    row["Year R&D"] = yearRd;
    const pct = rdSharePct(yearRd, yearTotal);
    row["%"] = pct === null ? "" : pct;
    return row;
  });

  const weekNoteRows = (overview.weekNotes ?? [])
    .slice()
    .sort(
      (a, b) =>
        a.isoWeek - b.isoWeek ||
        a.lastName.localeCompare(b.lastName) ||
        a.firstName.localeCompare(b.firstName),
    )
    .map((note) => ({
      Week: note.isoWeek,
      Employee: formatPersonName(note),
      Description: note.note,
    }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    detailRows.length > 0
      ? XLSX.utils.json_to_sheet(detailRows)
      : XLSX.utils.aoa_to_sheet([ALL_ENTRIES_HEADERS.slice()]),
    "All Entries",
  );
  XLSX.utils.book_append_sheet(
    workbook,
    overviewRows.length > 0
      ? XLSX.utils.json_to_sheet(overviewRows)
      : XLSX.utils.aoa_to_sheet([
          [
            "FirstName",
            "LastName",
            "Email",
            ...MONTHS_EN.flatMap((m) => [m, `${m} R&D`]),
            "Year total",
            "Year R&D",
            "%",
          ],
        ]),
    `Overview ${year}`,
  );
  XLSX.utils.book_append_sheet(
    workbook,
    weekNoteRows.length > 0
      ? XLSX.utils.json_to_sheet(weekNoteRows)
      : XLSX.utils.aoa_to_sheet([WEEK_NOTES_HEADERS.slice()]),
    WEEK_NOTES_SHEET,
  );

  const filename = `Stunden_${safeName(overview.projectName)}_${year}.xlsx`;
  downloadWorkbook(workbook, filename);
  return filename;
}
